import { useState } from "react";

import { DealIdeaCard } from "./DealIdeaCard";
import { IndustryPill } from "./IndustryPill";

const industries = [
    { id: "hotels", label: "Hotels", icon: "/images/hotel-2923328-1.svg" },
    { id: "cafe", label: "Café", icon: "/images/coffee-cup-7931536-1.svg" },
    { id: "retail", label: "Retail", icon: "/images/retail.svg" },
    { id: "entertainment", label: "Entertainment", icon: "/images/entertainment.svg" },
    { id: "restaurant", label: "Restaurant", icon: "/images/restaurant.svg" },
    { id: "auto", label: "Auto", icon: "/images/vector-9.svg" },
    { id: "home-services", label: "Home Services", icon: "/images/vector-11.svg" },
    { id: "spa-wellness", label: "Spa & Wellness", icon: "/images/vector-12.svg" },
    { id: "pet-stores", label: "Pet Stores", icon: "/images/pet-shop-6312282-1.svg" },
  ];

const dealIdeasMap: Record<
  string,
  { title: string; description: string }[]
> = {
  hotels: [
    {
      title: "20% Off Mid-Week Stays",
      description: "Drive occupancy on slow nights by attracting flexible travelers and staycationers.",
    },
    {
      title: "Free Breakfast with a 2-Night Booking",
      description: " Increase average stay length and keep guests on-property for future dining.",
    },
    {
      title: " $50 Dining Credit on Direct Bookings",
      description: "Avoid third-party commissions and drive immediate spend at your in-house restaurant.",
    },
  ],
  cafe: [
    {
      title: "Buy One, Get One 50% Off (2 PM - 5 PM)",
      description: "Turn the afternoon lull into a rush by encouraging coworkers to visit together.",
    },
    {
      title: "Free Pastry with Any Large Drink",
      description: "Introduce your high-margin food menu to build profitable new buying habits.",
    },
    {
      title: " $2 Off Any Breakfast Combo",
      description: " Turn morning commuters into loyal regulars by building a daily breakfast routine.",
    },
  ],
  retail: [
    {
      title: "Save $15 When You Spend $75+",
      description: "Instantly boost average order value by giving shoppers a reason to add one more item.",
    },
    {
      title: "Buy 2, Get 1 Free on Clearance",
      description: "Move stale inventory fast to free up valuable shelf space and cash flow.",
    },
    {
      title: "10% Off Entire Purchase (Followers Only)",
      description: "Trade a small discount for a loyal app follower you can market to all year for free.",
    },
  ],
  entertainment: [
    {
      title: "Half-Price Tuesdays",
      description: "Transform your deadest night into a community staple and build weekly habits.",
    },
    {
      title: "4-Person Family Pass for $40",
      description: "Attract groups who will spend significantly more on high-margin food and drinks.",
    },
    {
      title: "Buy 1 Hour, Get 30 Mins Free",
      description: "Keep guests in the venue longer, increasing the likelihood of concession sales.",
    },
  ],
  restaurant: [
    {
      title: "Free Appetizer with 2 Entrees (Mondays)",
      description: "Pack the dining room on slow nights and guarantee parties of two or more.",
    },
    {
      title: "$15 Express Lunch Combo",
      description: "Win the local lunch rush by attracting busy office workers who need a fast, high-value meal.",
    },
    {
      title: "Half-Price Bottles of Wine with Dinner",
      description: "Draw in date-night crowds while driving massive sales on full-priced meals.",
    },
  ],
  auto: [
    {
      title: "$39.99 Standard Oil Change",
      description: "Get new customers into the shop for routine maintenance to identify larger repair needs.",
    },
    {
      title: "Free Vacuum with Premium Wash",
      description: "Easily upsell customers from a basic exterior wash to a high-margin premium package.",
    },
    {
      title: "$20 Off Seasonal Tire Install",
      description: "Capture seasonal urgency and open the door for brake checks and fluid top-ups.",
    },
  ],
  "home-services": [
    {
      title: "$50 Off Any Repair Service Call",
      description: "Break the friction of upfront costs and get your technician through the front door.",
    },
    {
      title: "$99 Seasonal HVAC Tune-Up",
      description: "Fill your schedule during shoulder seasons and catch major repairs early.",
    },
    {
      title: "15% Off Full Home Deep Clean",
      description: "Lower the barrier on high-priced services while maintaining incredible profit margins.",
    },
  ],
  "spa-wellness": [
    {
      title: "$20 Off Next-Day Massages",
      description: "Use a flash sale to fill empty slots and keep your therapists booked and earning.",
    },
    {
      title: "Free Deep Conditioning with Color Service",
      description: "Highlight a high-margin add-on they’ll happily pay full price for next time.",
    },
    {
      title: "2-for-1 Pedicures on Wednesdays",
      description: "Double your customer base and fill seats by turning a solo trip into a social outing.",
    },
  ],
  "pet-stores": [
    {
      title: "15% Off Premium Dog Food",
      description: "Win their recurring food business, and they’ll buy their toys and accessories from you too.",
    },
    {
      title: "Buy 2 Get 1 Free on Treats",
      description: "Drive volume on high-margin inventory and significantly boost total checkout value.",
    },
    {
      title: "$10 Off Full Grooming Service",
      description: "Give pet owners the extra push they need to switch from their current groomer to you.",
    },
  ],
};

export const DealIdeasSection = () => {
  const [selectedIndustry, setSelectedIndustry] = useState("restaurant");

  const cards = dealIdeasMap[selectedIndustry];

  return (
    <div>
      <div className="hidden lg:block">
      <section className="w-full flex justify-center pb-20">
      <div className="w-full max-w-[1700px] ">
        <div
          className="
            relative overflow-hidden rounded-[0px]
            bg-[linear-gradient(180deg,#B90000_50%,#FF0000_100%)]
            px-8 md:px-12 pt-14 pb-12 
          "
        >
          {/* wave overlays */}
          <img
            src="/images/group.svg"
            alt=""
            className="absolute left-0 top-[-550px] w-[100%] opacity-85 pointer-events-none"
            aria-hidden="true"
          />
         

          <div className="relative z-10 ">
            {/* label */}
            <div className="flex items-center justify-center gap-3 text-white/95">
              <span className="w-[14px] h-[14px] rounded-full bg-white" />
              <span className="text-[16px] font-medium">Proven Offers</span>
            </div>

            {/* heading */}
            <h2 className="mt-6 text-center text-white text-[54px] md:text-[58px] leading-[1.08] font-medium tracking-[-1.2px]">
              Deal Ideas for Your Industry
            </h2>

            <p className="mt-3 text-center text-white/95 text-[22px] leading-[1.4]">
              Get inspired. See what works for businesses like yours.
            </p>

            {/* industry pills */}
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {industries.map((industry) => (
                <IndustryPill
                key={industry.id}
                label={industry.label}
                icon={industry.icon}
                active={selectedIndustry === industry.id}
                onClick={() => setSelectedIndustry(industry.id)}
              />
              ))}
            </div>

            {/* cards */}
            <div className="mt-14 grid grid-cols-1  md:grid-cols-3 gap-5">
              {cards.map((card, index) => (
                <DealIdeaCard
                  key={index}
                  title={card.title}
                  description={card.description}
                />
              ))}
            </div>

            {/* bottom CTA pill 
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                className="
                  min-w-[404px] h-[54px] rounded-full bg-white
                  px-8 text-[18px] font-medium text-[#E00000]
                  shadow-[0px_10px_24px_rgba(0,0,0,0.08)] hover:scale-105
    transition
                "
              >
                Tap to publish a coupon based on your goal.
              </button>
            </div>
            */}
          </div>
        </div>

        
      </div>
      <style>{`

   

div.transbox {
  background-color: rgba(193, 120, 120, 0.14);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  
  font-weight: bold;
} 
 


@keyframes floatSlow {
          0%,100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-16px);
          }
        }

        @keyframes floatSlowReverse {
          0%,100% {
            transform: translateY(-10px);
          }
          50% {
            transform: translateY(10px);
          }
        }

        .animate-floatSlow {
          animation: floatSlow 6s ease-in-out infinite;
        }
          .animate-floatSlowDelay {
          animation: floatSlow 5s ease-in-out infinite;
        }

        .animate-floatSlowReverse {
          animation: floatSlowReverse 7s ease-in-out infinite;
        }

 `}</style>
    </section>
      </div>


      <div className="block lg:hidden">
      <section className="w-full flex justify-center pb-20">
      <div className="w-full max-w-[1700px] ">
        <div
          className="
            relative overflow-hidden rounded-[0px]
            bg-[linear-gradient(180deg,#B90000_0%,#FF0000_100%)]
             pt-14 pb-12 
          "
        >
          {/* wave overlays */}
           <div className="absolute inset-0 pointer-events-none overflow-hidden">

      {/* red gradient background */}
      <img
        src="/images/Webp/rectangle-34625682.webp"
        alt=""
        className="absolute w-full h-full object-cover"
      />

      {/* wave pattern */}
      <img
        src="/images/waveMobile.svg"
        alt=""
        className="absolute right-0 top-0 w-[100%] "
      />

    </div>

          <div className="relative z-10 ">
            {/* label */}
            <div className="flex justify-center items-center  gap-2 text-white mb-6 px-6">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          <span className="text-[16px]">Proven Offers</span>
        </div>
           

            {/* heading */}
            <h2 className="px-6 mt-6 text-center text-white text-[30px] md:text-[58px] leading-[1.08] font-medium tracking-[-1.2px]">
              Deal Ideas for Your Industry
            </h2>

            <p className="px-6 mt-3 text-center text-white/95 text-[12px] leading-[1.4]">
              Get inspired. See what works for businesses like yours.
            </p>

            {/* industry pills */}
            <div className="mt-10 flex flex-wrap justify-center gap-2 ">
              {industries.map((industry) => (
                <IndustryPill
                key={industry.id}
                label={industry.label}
                icon={industry.icon}
                active={selectedIndustry === industry.id}
                onClick={() => setSelectedIndustry(industry.id)}
              />
              ))}
            </div>

            {/* cards */}
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-2 px-6">
              {cards.map((card, index) => (
                <DealIdeaCard
                  key={index}
                  title={card.title}
                  description={card.description}
                />
              ))}
            </div>

            {/* bottom CTA pill 
            <div className="mt-10 px-6 flex justify-center ">
              <button
                type="button"
                className="
                w-[360px] h-[54px] rounded-full bg-white
                   text-[14px] font-medium text-[#E00000]
                  shadow-[0px_10px_24px_rgba(0,0,0,0.08)] hover:scale-105
    transition
                "
              >
                Tap to publish a coupon based on your goal.
              </button>
            </div>
            */}
          </div>
        </div>

        
      </div>
      <style>{`

   

div.transbox {
  background-color: rgba(193, 120, 120, 0.14);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  
  font-weight: bold;
} 
 
 

@keyframes floatSlow {
          0%,100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-16px);
          }
        }

        @keyframes floatSlowReverse {
          0%,100% {
            transform: translateY(-10px);
          }
          50% {
            transform: translateY(10px);
          }
        }

        .animate-floatSlow {
          animation: floatSlow 6s ease-in-out infinite;
        }
          .animate-floatSlowDelay {
          animation: floatSlow 5s ease-in-out infinite;
        }

        .animate-floatSlowReverse {
          animation: floatSlowReverse 7s ease-in-out infinite;
        }

 `}</style>
    </section>
      </div>
    </div>
    
  );
};